import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Paper, Button, CircularProgress, LinearProgress, Divider } from '@/ui';
import {
  CheckCircleIcon,
  ErrorIcon,
  RefreshIcon,
  AddIcon,
  AccountBalanceIcon as BankIcon,
} from '@/ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '@/components/Header';
import BottomTabBar from '@/components/BottomTabBar';
import { httpClient } from '@/shared/api/http';
import { getCustomerId } from '@/shared/lib/utils/getCustomerId';
import { buildOpenBankingFetchPayload } from '@/shared/lib/openBankingFetchPayload';
import { useAuth } from '@/shared/state/store';
import { OnboardingService } from '@/shared/api/services';

// ========================================
// TypeScript Type Definitions
// ========================================

interface OpenBankingResponse {
  success: boolean;
  providerLogoUri?: string;
  providerDisplayName?: string;
  status: string;
  successful: boolean;
  inProgress?: boolean;
  bankLink?: string;
}

interface BankData {
  providerLogoUri: string;
  providerDisplayName: string;
  status: string;
}

interface TransactionTotals {
  totalTransactions: number;
  incomeTransCount: number;
  expenseTransCount: number;
  totalIncome: number;
  totalExpenses: number;
}

interface LmxAccountResponse {
  id?: number;
  customerId?: number;
  accountId?: string;
  sessionId?: string;
  accountNumber?: string;
  institutionName?: string;
  lmxApiSearchDetailsId?: number;
  accounts?: Array<{
    accountId?: string;
    sessionId?: string;
  }>;
}

// ========================================
// Constants
// ========================================

const POLLING_INTERVAL = 5000; // 5 seconds to reduce request frequency
const POLLING_TIMEOUT = 300000; // 300 seconds to accommodate slower banks
const FETCH_REQUEST_TIMEOUT = 120000; // 120 seconds per fetch request

// ========================================
// Main Component
// ========================================

const ObConfirmTransactionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchId = searchParams.get('SearchID') || searchParams.get('searchID')  || searchParams.get('searchid');
  const outcome =
    searchParams.get('Outcome') ||
    searchParams.get('outcome') ||
    searchParams.get('OUTCOME');
  const isAbortedOutcome = typeof outcome === 'string' && outcome.toLowerCase() === 'aborted';
  const { openBankingOnly, user, logout } = useAuth();

  // State Management
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [pollStartTime, setPollStartTime] = useState<number | null>(null);
  const [bankData, setBankData] = useState<BankData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
const [totals, setTotals] = useState<TransactionTotals | null>(null);
const [totalsLoading, setTotalsLoading] = useState<boolean>(false);
const [confirming, setConfirming] = useState<boolean>(false);
const [accountId, setAccountId] = useState<string | null>(null);

  // Refs for cleanup
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollStartTimeRef = useRef<number | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const hasFetchedAccountsRef = useRef<boolean>(false);
  const isPollingRef = useRef<boolean>(false);
  const isFetchInFlightRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const abortedOutcomeHandledRef = useRef<boolean>(false);

  // ========================================
  // Polling Logic
  // ========================================

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort();
      } catch {
        // ignore abort failures
      }
      abortControllerRef.current = null;
    }
    isFetchInFlightRef.current = false;
    pollStartTimeRef.current = null;
    setIsPolling(false);
    isPollingRef.current = false;
  }, []);

  const fetchTotals = useCallback(async (resolvedAccountId?: string) => {
    setTotalsLoading(true);
    try {
      const customerId = await getCustomerId();
      if (!customerId) {
        throw new Error('Unable to identify customer');
      }

      const accountToUse = resolvedAccountId || accountId;

      if (!accountToUse) {
        throw new Error('Missing account for totals request.');
      }

      const response = await httpClient.get<
        | {
            customerId: number;
            incomeTransCount?: number;
            expenseTransCount?: number;
            totalIncome?: number;
            totalExpenses?: number;
          }
        | Array<{
            customerId: number;
            incomeTransCount?: number;
            expenseTransCount?: number;
            totalIncome?: number;
            totalExpenses?: number;
          }>
      >('/iehub-customer/api/v1/lmx-acc-trans-totals-v', {
        params: {
          customerId: Number(customerId),
          accountId: accountToUse
        }
      });

      const payload = Array.isArray(response.data) ? response.data[0] : response.data;
      const incomeTransCount = payload?.incomeTransCount ?? 0;
      const expenseTransCount = payload?.expenseTransCount ?? 0;
      const totalIncome = payload?.totalIncome ?? 0;
      const totalExpenses = payload?.totalExpenses ?? 0;

      setAccountId(accountToUse);
      setTotals({
        totalTransactions: incomeTransCount + expenseTransCount,
        incomeTransCount,
        expenseTransCount,
        totalIncome,
        totalExpenses
      });
    } catch (err: any) {
      console.error('Totals fetch error:', err);
      const message = err.response?.data?.message || 'Failed to load transaction totals. Please try again.';
      setError(message);
    } finally {
      setTotalsLoading(false);
    }
  }, [accountId]);

  const deriveAccountSession = (data: LmxAccountResponse | LmxAccountResponse[]) => {
    const payload = Array.isArray(data) ? data[0] : data;
    const derivedAccountId =
      payload?.accountId ||
      payload?.accounts?.[0]?.accountId ||
      null;

    if (derivedAccountId) {
      setAccountId(derivedAccountId);
    }

    return { derivedAccountId };
  };

  const fetchAccountAndTotals = useCallback(async () => {
    try {
      const customerId = await getCustomerId();
      if (!customerId) {
        throw new Error('Unable to identify customer');
      }

      const accountResp = await httpClient.get<LmxAccountResponse | LmxAccountResponse[]>(
        '/iehub-customer/api/v1/ie-lmx-accounts',
        {
          params: { customerId: Number(customerId) }
        }
      );

      const { derivedAccountId } = deriveAccountSession(accountResp.data);

      if (!derivedAccountId) {
        throw new Error('Missing account information from LMX accounts response.');
      }

      await fetchTotals(derivedAccountId);
    } catch (err: any) {
      console.error('Account fetch error:', err);
      const message = err.response?.data?.message || err.message || 'Failed to load account details. Please try again.';
      setError(message);
    }
  }, [fetchTotals]);

  const fetchOpenBankingData = useCallback(async () => {
    // Prevent overlapping poll calls: our polling interval is shorter than axios timeout,
    // so without this, multiple POSTs stack up before the first one finishes/times out.
    if (isFetchInFlightRef.current) return;
    isFetchInFlightRef.current = true;
    try {
      const customerId = await getCustomerId();

      if (!customerId) {
        throw new Error('Unable to identify customer');
      }

      // Abort any previous in-flight request (defensive) and create a fresh controller.
      if (abortControllerRef.current) {
        try {
          abortControllerRef.current.abort();
        } catch {
          // ignore abort failures
        }
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const response = await httpClient.post<OpenBankingResponse>(
        '/iehub-customer/api/v1/openbanking/fetch',
        buildOpenBankingFetchPayload(Number(customerId), searchId ?? '', user?.isPartner),
        {
          signal: controller.signal,
          timeout: FETCH_REQUEST_TIMEOUT
        }
      );

      if (!isMountedRef.current) return;

      const data = response.data;

      // Check if processing is complete
      if (data.status === 'Complete' && data.successful) {
        if (hasFetchedAccountsRef.current) return;
        hasFetchedAccountsRef.current = true;

        stopPolling();
        setBankData({
          providerLogoUri: data.providerLogoUri || '',
          providerDisplayName: data.providerDisplayName || 'Your Bank',
          status: data.status
        });
        // After successful connection, load account + totals
        fetchAccountAndTotals();
        return;
      }

      // Check if still in progress
      if (data.inProgress) {
        // Continue polling - status will be updated on next poll
        return;
      }

      // Check for failure
      if (!data.successful && !data.inProgress) {
        stopPolling();
        setError('Bank connection failed. Please try again.');
        return;
      }

    } catch (err: any) {
      if (!isMountedRef.current) return;
      console.error('Polling error:', err);
      stopPolling();
      const errorMessage = err.response?.data?.message || 'Failed to fetch bank data. Please try again.';
      setError(errorMessage);
    }
    finally {
      // Clear in-flight state if we're still the active request
      isFetchInFlightRef.current = false;
      abortControllerRef.current = null;
    }
  }, [searchId, stopPolling, fetchAccountAndTotals, user?.isPartner]);

  const startPolling = useCallback(() => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    setIsPolling(true);
    const startedAt = Date.now();
    pollStartTimeRef.current = startedAt;
    setPollStartTime(startedAt);
    setError(null);
    setProgress(0);
    hasFetchedAccountsRef.current = false;

    // Initial fetch
    fetchOpenBankingData();

    // Set up polling interval
    pollingIntervalRef.current = setInterval(() => {
      if (!isMountedRef.current) {
        stopPolling();
        return;
      }

      // Check for timeout
      const elapsed =
        pollStartTimeRef.current !== null ? Date.now() - pollStartTimeRef.current : 0;
      const newProgress = Math.min((elapsed / POLLING_TIMEOUT) * 100, 100);
      setProgress(newProgress);

      if (elapsed >= POLLING_TIMEOUT) {
        stopPolling();
        setError('Connection timed out. The bank is taking longer than expected. Please try again.');
        return;
      }

      fetchOpenBankingData();
    }, POLLING_INTERVAL);

  }, [fetchOpenBankingData, stopPolling]);

  // ========================================
  // Progress Update Effect
  // ========================================

  useEffect(() => {
    if (!isPolling || !pollStartTime) return;

    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - pollStartTime;
      const newProgress = Math.min((elapsed / POLLING_TIMEOUT) * 100, 100);
      setProgress(newProgress);

      if (elapsed >= POLLING_TIMEOUT) {
        stopPolling();
        setError('Connection timed out. The bank is taking longer than expected. Please try again.');
      }
    }, 100);

    return () => clearInterval(progressInterval);
  }, [isPolling, pollStartTime, stopPolling]);

  // ========================================
  // Effects
  // ========================================

  useEffect(() => {
    isMountedRef.current = true;

    // Bank redirect may include an Outcome query param. If the user aborted
    // the flow, return them to the Open Banking start page.
    //
    // Agent-led (Open Banking-only) journeys must log the user out immediately
    // on cancel/abort and redirect them back to Auth.
    if (isAbortedOutcome) {
      if (abortedOutcomeHandledRef.current) {
        return () => {
          isMountedRef.current = false;
          stopPolling();
        };
      }
      abortedOutcomeHandledRef.current = true;
      if (openBankingOnly) {
        stopPolling();
        void (async () => {
          try {
            // Legacy parity: if the user arrived via creditor Open Banking invitation,
            // update invitation status when the journey is cancelled/rejected (Outcome=aborted).
            try {
              if (typeof user?.userId === 'number' && user.userId > 0) {
                const invites = await OnboardingService.getOpenBankInvitationsByUserId(user.userId, {
                  skipErrorCapture: true,
                });
                const first = Array.isArray(invites) ? invites[0] : undefined;
                if (first && typeof first.id === 'number') {
                  const now = Date.now();
                  const isPartnerUser = String(user?.isPartner ?? 'N').trim().toUpperCase() === 'Y';
                  const updates = isPartnerUser
                    ? {
                        partnersStatus: 'Partner Rejected',
                        partnersStatusDate: now,
                      }
                    : {
                        status: 'Customer Rejected',
                        customerStatusDate: now,
                      };
                  await OnboardingService.updateOpenBankInvitation(first, updates, { skipErrorCapture: true });
                }
              }
            } catch (inviteError) {
              // Non-blocking: do not prevent logout/navigation.
              // eslint-disable-next-line no-console
              console.warn('Failed to update Open Banking invitation status (rejected)', inviteError);
            }

            await logout({ source: 'manual' });
          } finally {
            navigate('/auth', { replace: true });
          }
        })();
      } else {
        navigate('/open-banking', { replace: true });
      }
      return () => {
        isMountedRef.current = false;
        stopPolling();
      };
    }

    // Validate SearchID on mount
    if (!searchId) {
      setError('No search ID found. Please start the Open Banking process again.');
      return () => {
        isMountedRef.current = false;
        stopPolling();
      };
    }

    // If this SearchID has already completed previously, skip polling and go
    // straight to the summary page so we don't trigger the fetch job again.
    try {
      const completionKey = `ob_complete_${searchId}`;
      const storedComplete = window.localStorage.getItem(completionKey);

      if (storedComplete === '1') {
        navigate(`/ob-summary?SearchID=${encodeURIComponent(searchId)}`);
        return () => {
          isMountedRef.current = false;
          stopPolling();
        };
      }
    } catch {
      // Ignore localStorage access errors and fall back to normal polling flow
    }

    // Start polling automatically for fresh SearchIDs
    startPolling();

    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchId, isAbortedOutcome, openBankingOnly, logout, navigate, stopPolling, user]);

  // When the bank connection has completed successfully, persist a completion
  // flag and bank display info, then redirect the user to the summary page.
  useEffect(() => {
    if (!bankData || error) {
      return;
    }

    if (!searchId) {
      navigate(openBankingOnly ? '/open-banking' : '/dashboard');
      return;
    }

    const completionKey = `ob_complete_${searchId}`;
    const bankKey = `ob_bank_${searchId}`;

    try {
      window.localStorage.setItem(completionKey, '1');
      window.localStorage.setItem(
        bankKey,
        JSON.stringify({
          providerLogoUri: bankData.providerLogoUri,
          providerDisplayName: bankData.providerDisplayName,
          status: bankData.status
        })
      );
    } catch {
      // Ignore localStorage write errors; the flow can still continue.
    }

    navigate(`/ob-summary?SearchID=${encodeURIComponent(searchId)}`);
  }, [bankData, error, navigate, openBankingOnly, searchId]);

  // ========================================
  // Event Handlers
  // ========================================

  const handleRetry = () => {
    setError(null);
    setBankData(null);
    setTotals(null);
    setAccountId(null);
    startPolling();
  };

  const handleBackToDashboard = () => {
    navigate(openBankingOnly ? '/open-banking' : '/dashboard');
  };

  const handleStartOver = () => {
    setTotals(null);
    setAccountId(null);
    navigate('/open-banking');
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const customerId = await getCustomerId();

      if (!customerId) {
        throw new Error('Unable to identify customer');
      }

      // Legacy parity:
      // - Creditor/agent-invited ("Open Banking-only") users are sharing transactions with the inviting company
      //   and should NOT import them into their own budget (skip LMX update-to-main).
      // - Normal customers should import transactions into the main budget (call LMX update-to-main).
      if (!openBankingOnly) {
        await httpClient.post('/iehub-customer/api/v1/lmx-data-update-to-main', {
          customerId: Number(customerId)
        });
      }

      // Defensive parity: update invitation status on submit for creditor-invited users.
      // (Primary submit handler lives in `obSummary.tsx`, but keep this in sync.)
      const isPartnerUser = String(user?.isPartner ?? 'N').trim().toUpperCase() === 'Y';
      // Team rule: only agent-invited Open Banking journeys should touch ie-openbank-invitation.
      if (openBankingOnly && typeof user?.userId === 'number' && user.userId > 0) {
        try {
          const invites = await OnboardingService.getOpenBankInvitationsByUserId(user.userId, {
            skipErrorCapture: true,
          });
          const first = Array.isArray(invites) ? invites[0] : undefined;
          if (first && typeof first.id === 'number') {
            const now = Date.now();
            const updates = isPartnerUser
              ? {
                  partnersStatus: 'Submitted',
                  partnersStatusDate: now,
                }
              : {
                  status: 'Submitted',
                  customerStatusDate: now,
                };
            await OnboardingService.updateOpenBankInvitation(
              first,
              updates,
              { skipErrorCapture: true }
            );
          }
        } catch (inviteError) {
          // Non-blocking: do not prevent the customer from completing the journey.
          // eslint-disable-next-line no-console
          console.warn('Failed to update Open Banking invitation status', inviteError);
        }
      }

      navigate(openBankingOnly ? '/ob-thank-you' : '/update-budget');
    } catch (err: any) {
      console.error('Confirm error:', err);
      const message = err.response?.data?.message || 'Failed to confirm transactions. Please try again.';
      setError(message);
    } finally {
      setConfirming(false);
    }
  };

  const handleAddAnotherBank = () => {
    navigate('/open-banking');
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2
    }).format(value);

  // ========================================
  // Render: Loading/Polling State
  // ========================================

  const LoadingView = () => (
    <Paper data-testid="pages-private-from-dashboard-loading-view-container-action-1"
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: { xs: 4, md: 6 },
        gap: 4,
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        maxWidth: '480px',
        mx: 'auto',
        backgroundColor: '#fff'
      }}
    >
      {/* Bank Icon with Pulse Animation */}
      <Box data-testid="pages-private-from-dashboard-loading-view-container-action-2"
        sx={{
          position: 'relative',
          width: 96,
          height: 96,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box data-testid="pages-private-from-dashboard-loading-view-container-action-3"
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            backgroundColor: '#e6f7f7',
            animation: 'pulse 2s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { transform: 'scale(1)', opacity: 1 },
              '50%': { transform: 'scale(1.15)', opacity: 0.7 }
            }
          }}
        />
        <BankIcon sx={{ fontSize: 48, color: '#00474d', position: 'relative', zIndex: 1 }} />
      </Box>

      <Box data-testid="pages-private-from-dashboard-loading-view-container-action-4" sx={{ width: '100%', maxWidth: '360px' }}>
        <Typography data-testid="pages-private-from-dashboard-loading-view-text-connecting-to-your-bank"
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '20px', md: '24px' },
            color: '#00474d',
            mb: 1
          }}
        >
          Connecting to your bank
        </Typography>
        <Typography data-testid="pages-private-from-dashboard-loading-view-text-action-1"
          variant="body2"
          sx={{
            fontSize: '15px',
            color: '#64748b',
            mb: 3,
            lineHeight: 1.6
          }}
        >
          We're securely retrieving your transactions. This may take a few moments.
        </Typography>

        {/* Progress Bar Container */}
        <Box data-testid="pages-private-from-dashboard-loading-view-container-action-5" sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: '#e2e8f0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#00474d',
                borderRadius: 5,
                transition: 'transform 0.2s ease'
              }
            }}
          />
        </Box>

        <Box data-testid="pages-private-from-dashboard-loading-view-container-action-6" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography data-testid="pages-private-from-dashboard-loading-view-text-action-2" variant="caption" sx={{ color: '#94a3b8', fontSize: '13px' }}>
            {progress < 30 ? 'Authenticating...' : progress < 70 ? 'Fetching transactions...' : 'Almost there...'}
          </Typography>
          <Typography data-testid="pages-private-from-dashboard-loading-view-text-%" variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '13px' }}>
            {Math.round(progress)}%
          </Typography>
        </Box>
      </Box>

      <Typography data-testid="pages-private-from-dashboard-loading-view-text-🔒-your-data-is-encrypted-and-secure"
        variant="caption"
        sx={{
          color: '#94a3b8',
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        🔒 Your data is encrypted and secure
      </Typography>
    </Paper>
  );

  // ========================================
  // Render: Error State
  // ========================================

  const ErrorView = () => (
    <Paper data-testid="pages-private-from-dashboard-error-view-container-action-1"
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: { xs: 4, md: 6 },
        gap: 3,
        borderRadius: '16px',
        border: '1px solid #fecaca',
        maxWidth: '480px',
        mx: 'auto',
        backgroundColor: '#fff'
      }}
    >
      <Box data-testid="pages-private-from-dashboard-error-view-container-action-2"
        sx={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(239, 68, 68, 0.15)'
        }}
      >
        <ErrorIcon sx={{ fontSize: 44, color: '#dc2626' }} />
      </Box>

      <Box data-testid="pages-private-from-dashboard-error-view-container-action-3" sx={{ maxWidth: '320px' }}>
        <Typography data-testid="pages-private-from-dashboard-error-view-text-connection-failed"
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '20px', md: '22px' },
            color: '#1e293b',
            mb: 1.5
          }}
        >
          Connection Failed
        </Typography>
        <Typography data-testid="pages-private-from-dashboard-error-view-text-action"
          variant="body2"
          sx={{
            fontSize: '15px',
            color: '#64748b',
            lineHeight: 1.6
          }}
        >
          {error}
        </Typography>
      </Box>

      <Box data-testid="pages-private-from-dashboard-error-view-container-action-4" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: '280px', mt: 1 }}>
        <Button data-testid="pages-private-from-dashboard-error-view-btn-handle-retry"
          variant="contained"
          onClick={handleRetry}
          startIcon={<RefreshIcon />}
          sx={{
            backgroundColor: '#00474d',
            color: '#fff',
            textTransform: 'none',
            borderRadius: '12px',
            py: 1.5,
            fontWeight: 600,
            fontSize: '15px',
            boxShadow: '0 2px 8px rgba(0, 71, 77, 0.25)',
            '&:hover': {
              backgroundColor: '#003d42',
              boxShadow: '0 4px 12px rgba(0, 71, 77, 0.35)'
            }
          }}
        >
          Try Again
        </Button>
        <Button data-testid="pages-private-from-dashboard-error-view-btn-handle-start-over"
          variant="outlined"
          onClick={handleStartOver}
          sx={{
            borderColor: '#e2e8f0',
            color: '#475569',
            textTransform: 'none',
            borderRadius: '12px',
            py: 1.5,
            fontWeight: 600,
            fontSize: '15px',
            '&:hover': {
              borderColor: '#cbd5e1',
              backgroundColor: '#f8fafc'
            }
          }}
        >
          Choose Different Bank
        </Button>
        <Button data-testid="pages-private-from-dashboard-error-view-btn-handle-back-to-dashboard"
          variant="text"
          onClick={handleBackToDashboard}
          sx={{
            color: '#64748b',
            textTransform: 'none',
            py: 1,
            fontSize: '14px',
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#475569'
            }
          }}
        >
          {openBankingOnly ? 'Cancel' : 'Back to Dashboard'}
        </Button>
      </Box>
    </Paper>
  );

  // ========================================
  // Render: Success State
  // ========================================

  const SuccessView = () => (
    <Box data-testid="pages-private-from-dashboard-success-view-container-action-1"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100%',
        maxWidth: '900px',
        mx: 'auto'
      }}
    >
      {/* Page Header */}
      <Box data-testid="pages-private-from-dashboard-success-view-container-action-2" sx={{ mb: 1 }}>
        <Typography data-testid="pages-private-from-dashboard-success-view-text-confirm-your-transactions"
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '22px', md: '26px' },
            color: '#00474d',
            mb: 1
          }}
        >
          Confirm Your Transactions
        </Typography>
        <Typography data-testid="pages-private-from-dashboard-success-view-text-action-1"
          variant="body2"
          sx={{
            fontSize: '15px',
            color: '#64748b',
            lineHeight: 1.6
          }}
        >
          {openBankingOnly
            ? 'Review the summary below and confirm to finish linking your bank.'
            : 'Review the summary below and confirm to proceed with your budget analysis.'}
        </Typography>
      </Box>

      {/* Cards Container */}
      <Box data-testid="pages-private-from-dashboard-success-view-container-action-3"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
          gap: 3
        }}
      >
        {/* Connected Bank Card */}
        <Paper data-testid="pages-private-from-dashboard-success-view-container-action-4"
          elevation={0}
          sx={{
            p: 3,
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            backgroundColor: '#fff',
            height: 'fit-content'
          }}
        >
          <Typography data-testid="pages-private-from-dashboard-success-view-text-connected-accounts"
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              color: '#1e293b',
              mb: 2.5
            }}
          >
            Connected Accounts
          </Typography>

          {/* Bank Connection Status */}
          <Box data-testid="pages-private-from-dashboard-success-view-container-action-5"
            sx={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '12px',
              p: 2,
              mb: 2
            }}
          >
            <Box data-testid="pages-private-from-dashboard-success-view-container-action-6" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box data-testid="pages-private-from-dashboard-success-view-container-action-7"
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  backgroundColor: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 26, color: '#16a34a' }} />
              </Box>
              <Box data-testid="pages-private-from-dashboard-success-view-container-action-8" sx={{ flex: 1, minWidth: 0 }}>
                <Typography data-testid="pages-private-from-dashboard-success-view-text-action-2"
                  sx={{
                    fontWeight: 700,
                    fontSize: '15px',
                    color: '#166534',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {bankData?.providerDisplayName || 'Your Bank'}
                </Typography>
                <Typography data-testid="pages-private-from-dashboard-success-view-text-connected-successfully"
                  variant="caption"
                  sx={{
                    color: '#15803d',
                    fontSize: '13px'
                  }}
                >
                  Connected successfully
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Add Another Bank Button */}
          <Button data-testid="pages-private-from-dashboard-success-view-btn-handle-add-another-bank"
            variant="outlined"
            onClick={handleAddAnotherBank}
            startIcon={<AddIcon />}
            fullWidth
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              borderStyle: 'dashed',
              borderColor: '#94a3b8',
              borderWidth: '1.5px',
              color: '#475569',
              fontWeight: 600,
              fontSize: '14px',
              py: 1.25,
              '&:hover': {
                borderColor: '#00474d',
                backgroundColor: '#f0fdfa',
                color: '#00474d'
              }
            }}
          >
            Add Another Bank
          </Button>
        </Paper>

        {/* Transaction Summary Card */}
        <Paper data-testid="pages-private-from-dashboard-success-view-container-action-9"
          elevation={0}
          sx={{
            p: 3,
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            backgroundColor: '#fff'
          }}
        >
          <Typography data-testid="pages-private-from-dashboard-success-view-text-transaction-summary"
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              color: '#1e293b',
              mb: 2.5
            }}
          >
            Transaction Summary
          </Typography>

          {totalsLoading ? (
            <Box data-testid="pages-private-from-dashboard-success-view-container-action-10"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                gap: 2
              }}
            >
              <CircularProgress size={32} sx={{ color: '#00474d' }} />
              <Typography data-testid="pages-private-from-dashboard-success-view-text-loading-transaction-summary..." variant="body2" sx={{ color: '#64748b' }}>
                Loading transaction summary...
              </Typography>
            </Box>
          ) : (
            <>
              {/* Stats Grid */}
              <Box data-testid="pages-private-from-dashboard-success-view-container-action-11"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  gap: 2,
                  mb: 3
                }}
              >
                <StatCard
                  label="Total Transactions"
                  value={totals?.totalTransactions ?? 0}
                  variant="neutral"
                />
                <StatCard
                  label="Credit Transactions"
                  value={totals?.incomeTransCount ?? 0}
                  variant="success"
                />
                <StatCard
                  label="Debit Transactions"
                  value={totals?.expenseTransCount ?? 0}
                  variant="warning"
                />
              </Box>

              {/* Totals Section */}
              <Box data-testid="pages-private-from-dashboard-success-view-container-action-12" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box data-testid="pages-private-from-dashboard-success-view-container-action-13"
                  sx={{
                    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    borderRadius: '12px',
                    p: 2.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid #a7f3d0'
                  }}
                >
                  <Box data-testid="pages-private-from-dashboard-success-view-container-action-14">
                    <Typography data-testid="pages-private-from-dashboard-success-view-text-total-credits"
                      sx={{
                        color: '#166534',
                        fontWeight: 600,
                        fontSize: '14px',
                        mb: 0.5
                      }}
                    >
                      Total Credits
                    </Typography>
                    <Typography data-testid="pages-private-from-dashboard-success-view-text-money-received"
                      sx={{
                        color: '#15803d',
                        fontSize: '12px'
                      }}
                    >
                      Money received
                    </Typography>
                  </Box>
                  <Typography data-testid="pages-private-from-dashboard-success-view-text-action-3"
                    sx={{
                      color: '#166534',
                      fontWeight: 700,
                      fontSize: '20px'
                    }}
                  >
                    {formatCurrency(totals?.totalIncome ?? 0)}
                  </Typography>
                </Box>

                <Box data-testid="pages-private-from-dashboard-success-view-container-action-15"
                  sx={{
                    background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                    borderRadius: '12px',
                    p: 2.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid #fecaca'
                  }}
                >
                  <Box data-testid="pages-private-from-dashboard-success-view-container-action-16">
                    <Typography data-testid="pages-private-from-dashboard-success-view-text-total-debits"
                      sx={{
                        color: '#991b1b',
                        fontWeight: 600,
                        fontSize: '14px',
                        mb: 0.5
                      }}
                    >
                      Total Debits
                    </Typography>
                    <Typography data-testid="pages-private-from-dashboard-success-view-text-money-spent"
                      sx={{
                        color: '#b91c1c',
                        fontSize: '12px'
                      }}
                    >
                      Money spent
                    </Typography>
                  </Box>
                  <Typography data-testid="pages-private-from-dashboard-success-view-text-action-4"
                    sx={{
                      color: '#991b1b',
                      fontWeight: 700,
                      fontSize: '20px'
                    }}
                  >
                    {formatCurrency(totals ? Math.abs(totals.totalExpenses) : 0)}
                  </Typography>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box data-testid="pages-private-from-dashboard-success-view-container-action-17"
                sx={{
                  mt: 4,
                  pt: 3,
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'flex-end',
                  gap: 2
                }}
              >
                <Button data-testid="pages-private-from-dashboard-success-view-btn-handle-back-to-dashboard"
                  variant="outlined"
                  onClick={handleBackToDashboard}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '10px',
                    borderColor: '#e2e8f0',
                    color: '#64748b',
                    fontWeight: 600,
                    px: 3,
                    py: 1.25,
                    order: { xs: 2, sm: 1 },
                    '&:hover': {
                      borderColor: '#cbd5e1',
                      backgroundColor: '#f8fafc'
                    }
                  }}
                >
                  {openBankingOnly ? 'Cancel' : 'Cancel'}
                </Button>
                <Button data-testid="pages-private-from-dashboard-success-view-btn-handle-confirm"
                  variant="contained"
                  onClick={handleConfirm}
                  disabled={confirming || totalsLoading}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '10px',
                    backgroundColor: '#d4e03b',
                    color: '#003d3b',
                    fontWeight: 700,
                    px: 4,
                    py: 1.25,
                    fontSize: '15px',
                    order: { xs: 1, sm: 2 },
                    boxShadow: '0 2px 8px rgba(212, 224, 59, 0.4)',
                    '&:hover': {
                      backgroundColor: '#c5d630',
                      boxShadow: '0 4px 12px rgba(212, 224, 59, 0.5)'
                    },
                    '&:disabled': {
                      backgroundColor: '#e2e8f0',
                      color: '#94a3b8'
                    }
                  }}
                >
                  {confirming ? (
                    <Box data-testid="pages-private-from-dashboard-success-view-container-action-18" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} sx={{ color: '#003d3b' }} />
                      <span>Confirming...</span>
                    </Box>
                  ) : (
                    openBankingOnly ? 'Submit' : 'Confirm & Continue'
                  )}
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );

  const StatCard: React.FC<{
    label: string;
    value: number;
    variant: 'neutral' | 'success' | 'warning';
  }> = ({ label, value, variant }) => {
    const variantStyles = {
      neutral: {
        bg: '#f8fafc',
        border: '#e2e8f0',
        valueColor: '#1e293b',
        labelColor: '#64748b'
      },
      success: {
        bg: '#f0fdf4',
        border: '#bbf7d0',
        valueColor: '#166534',
        labelColor: '#15803d'
      },
      warning: {
        bg: '#fefce8',
        border: '#fef08a',
        valueColor: '#854d0e',
        labelColor: '#a16207'
      }
    };

    const styles = variantStyles[variant];

    return (
      <Box data-testid="pages-private-from-dashboard-stat-card-container-action-1"
        sx={{
          backgroundColor: styles.bg,
          border: `1px solid ${styles.border}`,
          borderRadius: '12px',
          p: 2,
          textAlign: 'center'
        }}
      >
        <Typography data-testid="pages-private-from-dashboard-stat-card-text-action-1-1"
          sx={{
            color: styles.valueColor,
            fontWeight: 700,
            fontSize: '28px',
            lineHeight: 1.2,
            mb: 0.5
          }}
        >
          {value.toLocaleString()}
        </Typography>
        <Typography data-testid="pages-private-from-dashboard-stat-card-text-action-2-1"
          sx={{
            color: styles.labelColor,
            fontWeight: 500,
            fontSize: '13px'
          }}
        >
          {label}
        </Typography>
      </Box>
    );
  };

  // ========================================
  // Determine Current View
  // ========================================

  const getCurrentContent = () => {
    if (error) {
      return <ErrorView />;
    }
    if (bankData) {
      return <SuccessView />;
    }
    return <LoadingView />;
  };

  // ========================================
  // Unified Page Layout
  // ========================================

  const PageLayout = () => {
    const isSuccessState = bankData && !error;

    return (
      <Box data-testid="pages-private-from-dashboard-page-layout-container-action-1-1"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: '#f8fafc'
        }}
      >
        <Header data-testid="pages-private-from-dashboard-page-layout-header-action-1" />

        <Box data-testid="pages-private-from-dashboard-page-layout-container-action-2-1"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: isSuccessState ? 'stretch' : 'center',
            justifyContent: isSuccessState ? 'flex-start' : 'center',
            overflow: 'auto',
            px: { xs: 2, md: 4 },
            py: { xs: 3, md: 4 },
            pb: { xs: 12, md: 4 }
          }}
        >
          {getCurrentContent()}
        </Box>

        {/* Mobile Bottom Nav */}
        <Box data-testid="pages-private-from-dashboard-page-layout-container-action-3-1" sx={{ display: { xs: 'block', md: 'none' } }}>
          <BottomTabBar data-testid="pages-private-from-dashboard-page-layout-nav-bar-action-1" />
        </Box>
      </Box>
    );
  };

  // ========================================
  // Main Render
  // ========================================

  return <PageLayout />;
};

export default ObConfirmTransactionPage;
